import { get } from "lodash";

export const getUpsellIdentifier = (module) =>
  get(module, "costDetails.identifier") ||
  get(module, "insurancePlan.identifier") ||
  null;

const samePackSet = (first = [], second = []) => {
  if (first.length !== second.length) return false;
  const sortedFirst = [...first].sort();
  const sortedSecond = [...second].sort();
  return sortedFirst.every((value, index) => value === sortedSecond[index]);
};

const getCombinationPricing = (modules = []) =>
  modules
    .map((module) => get(module, "costDetails.combinationPricing"))
    .find((value) => Array.isArray(value) && value.length > 0) || [];

/**
 * True when the upsell modules carry a Lumineis pack combination matrix, i.e.
 * the per-pack combination discount applies to them.
 */
export const isLumineisUpsells = (modules = []) =>
  modules.some((module) => {
    const pricing = get(module, "costDetails.combinationPricing");
    return Array.isArray(pricing) && pricing.length > 0;
  });

export const findCombination = (modules, packs) => {
  const pricing = getCombinationPricing(modules);
  return pricing.find((entry) => samePackSet(get(entry, "packs", []), packs));
};

/**
 * Rebuild upsell pricing for the currently selected pack combination.
 *
 * `cost` on each module is the standalone price (pack chosen alone). When packs
 * are combined, Alptis applies a discount, captured in `costDetails.combinationPricing`.
 * This resolves the combination matching the selected packs and returns the
 * discounted total plus a per-module price resolver.
 */
export const computeUpsellPricing = ({
  modules = [],
  selectedModules = [],
} = {}) => {
  const standaloneByIdentifier = {};
  modules.forEach((module) => {
    const identifier = getUpsellIdentifier(module);
    if (identifier) standaloneByIdentifier[identifier] = get(module, "cost", 0);
  });

  const selectedIdentifiers = selectedModules
    .map((module) => getUpsellIdentifier(module))
    .filter(Boolean);

  const standaloneTotal = selectedIdentifiers.reduce(
    (sum, identifier) => sum + (standaloneByIdentifier[identifier] || 0),
    0,
  );

  const combination = findCombination(
    modules.length ? modules : selectedModules,
    selectedIdentifiers,
  );
  const packCosts = get(combination, "packCosts", null);
  const total = combination
    ? get(combination, "upsellTotal", 0)
    : standaloneTotal;
  const hasDiscount = total < standaloneTotal;

  const costForModule = (module) => {
    const identifier = getUpsellIdentifier(module);
    const isSelected = selectedIdentifiers.includes(identifier);
    if (isSelected && packCosts && packCosts[identifier] != null) {
      return packCosts[identifier];
    }
    return get(module, "cost", 0);
  };

  return { total, hasDiscount, standaloneTotal, costForModule };
};

/**
 * Rewrite each selected module's `cost` to the combination-adjusted price for
 * the pack set it belongs to. Operates on a `{ [insurancePlanId]: module[] }`
 * map (the shape held in `project.fields.upsells`) and is idempotent, so it is
 * safe to apply to already-resolved data reloaded from persisted fields.
 */
export const resolveSelectedModulesCosts = (selectedModulesByPlan = {}) => {
  const resolved = {};
  Object.keys(selectedModulesByPlan).forEach((planId) => {
    const modules = selectedModulesByPlan[planId] || [];
    const { costForModule } = computeUpsellPricing({
      modules,
      selectedModules: modules,
    });
    resolved[planId] = modules.map((module) => ({
      ...module,
      cost: costForModule(module),
    }));
  });
  return resolved;
};
